import { useEffect, useRef, useState } from 'react';
import BrregApi from 'services/KjederegisteretAdminApi/BrregApi';
import { BrregEnhetResponse } from 'services/KjederegisteretAdminApi/BrregApi/types';

export type BrregWithDagligLeder = BrregEnhetResponse & { dagligLeder?: string };

interface UseBrregLookupResult {
  brregInfo?: BrregWithDagligLeder | undefined;
  isLoading: boolean;
  error?: unknown;
}

/**
 * Look up Brreg info for a given orgnr with stale guards.
 * Returns the first result merged with dagligLeder as a single object.
 */
export default function useBrregLookup(orgnr?: string | null): UseBrregLookupResult {
  const [result, setResult] = useState<BrregEnhetResponse[]>([]);
  const [dagligLeder, setDagligLeder] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const lookupReqIdRef = useRef(0);
  const dagligLederReqIdRef = useRef(0);

  useEffect(() => {
    if (!orgnr) {
      setResult([]);
      setDagligLeder(undefined);
      return;
    }
    const reqId = ++lookupReqIdRef.current;
    setIsLoading(true);
    setError(undefined);
    BrregApi.getByOrgNr(orgnr)
      .then((res) => {
        if (lookupReqIdRef.current !== reqId) return; // stale
        setResult(Array.isArray(res) ? res : []);
      })
      .catch((err) => {
        if (lookupReqIdRef.current !== reqId) return; // stale
        setResult([]);
        setError(err);
      })
      .finally(() => {
        if (lookupReqIdRef.current === reqId) setIsLoading(false);
      });

    const dReqId = ++dagligLederReqIdRef.current;
    BrregApi.getDagligLeder(orgnr)
      .then((dl) => {
        if (dagligLederReqIdRef.current !== dReqId) return; // stale
        setDagligLeder(dl);
      })
      .catch(() => {
        if (dagligLederReqIdRef.current !== dReqId) return; // stale
        setDagligLeder(undefined);
      });
  }, [orgnr]);

  const brregInfo = result[0] ? ({ ...result[0], dagligLeder } as BrregWithDagligLeder) : undefined;
  return { brregInfo, isLoading, error };
}
